import { MouseEvent, ReactElement } from 'react';
import { useDispatch } from 'react-redux';
import { removeCardProduct } from '@client/cart/redux/cart/redux';
import { useTranslate } from '@client/i18n/hooks';
import { ROUTES } from '@client/routes/components/Router/routes';
import { useOpenLink, useUrlCreator } from '@client/routes/hooks';
import { Alert } from '@lib/components/Alert';
import { Button, BUTTON_ICON_POSITION, BUTTON_SIZE, BUTTON_STYLE_TYPE } from '@lib/components/Button';
import { Icon, ICON_NAME, ICON_SIZE } from '@lib/components/Icon';
import { Modal } from '@lib/components/Modal';
import classNames from 'classnames';
import { ModalButtons } from 'b2b-components/core/components/Modal';
import { CONFIGURABLE_TYPE } from 'b2b-common/core/product/api/Product.types';
import { Props } from './';
import './ConfigurableButtons.scss';

export const ConfigurableButtons = ({
  product,
  className,
}: Props): ReactElement => {
  const t = useTranslate();
  const openLink = useOpenLink();
  const urlCreator = useUrlCreator();
  const dispatch = useDispatch();

  const { sku, supplierConfig, settings, cart } = product;
  const isProductStamp = settings?.configurableType === CONFIGURABLE_TYPE.stamp;
  const isProductCard = settings?.configurableType === CONFIGURABLE_TYPE.card;
  const partAuxiliaryId = supplierConfig?.partAuxiliaryId || null;
  const layoutPreviewUrl = supplierConfig?.layoutPreviewUrl || null;
  const configuratorPageEditUrl = urlCreator(ROUTES.productConfigurator, { sku, partAuxiliaryId });

  const handleOpenConfigurableProductEditPage = (event: MouseEvent<HTMLElement>) => {
    openLink(event, configuratorPageEditUrl);
  };

  const handleCardConfigureButtonClick = () => {
    dispatch(removeCardProduct({ sku, quantity: cart.quantity, partAuxiliaryId }));
  };

  return (
    <div className={classNames('CartProductConfigurableButtons', className)}>
      <Modal
        title={t('Customized product preview')}
        content={(onClose) => (
          <>
            <div
              className={classNames(
                'CartProductConfigurableButtons-ModalImageBox',
                {
                  'CartProductConfigurableButtons-ModalImageBox--Card': isProductCard,
                },
              )}
            >
              <img
                src={layoutPreviewUrl || undefined}
                alt={t('Customized product preview')}
                className={classNames(
                  'CartProductConfigurableButtons-ModalImage',
                  {
                    'CartProductConfigurableButtons-ModalImage--Stamp': isProductStamp,
                    'CartProductConfigurableButtons-ModalImage--Card': isProductCard,
                  },
                )}
              />
            </div>
            {isProductCard && (
              <Alert className='CartProductConfigurableButtons-ModalAlert'>
                {t('You cannot edit a Business Card from the basket, clicking the “Restart Configurator” button removes the position from the basket and lets you reconfigure your Business Card template')}
              </Alert>
            )}

            <ModalButtons
              secondary={{
                icon: ICON_NAME.edit,
                label: '',
                ...isProductStamp && {
                  onClick: handleOpenConfigurableProductEditPage,
                  label: t('Configure'),
                },
                ...isProductCard && {
                  onClick: handleCardConfigureButtonClick,
                  label: t('Remove and restart'),
                },
              }}
              primary={{
                onClick: onClose,
                label: t('Close'),
              }}
            />
          </>
        )}
        triggerObject={(
          <Button
            styleType={BUTTON_STYLE_TYPE.light}
            size={BUTTON_SIZE.small}
            isDisabled={!layoutPreviewUrl}
          >
            {t('Preview')}
          </Button>
        )}
      />

      {isProductStamp && (
        <Button
          styleType={BUTTON_STYLE_TYPE.light}
          iconPosition={BUTTON_ICON_POSITION.left}
          size={BUTTON_SIZE.small}
          onClick={handleOpenConfigurableProductEditPage}
          isDisabled={!partAuxiliaryId}
        >
          <Icon name={ICON_NAME.edit} size={ICON_SIZE.small} />
          {t('Configure')}
        </Button>
      )}
    </div>
  );
};
